// WizardConfirmationCard — review gate before spec generation.
// Shown when POST /wizard/app-idea/answer returns confirmationRequired:true.
// Two CTAs: accept (triggers confirmBuild) or edit (inline form → onEdit).

import React, { useState } from 'react';
import type {
  ConfirmationSummary,
  EnrichedRequirements,
} from '../../hooks/useSubscriberChat.js';

interface WizardConfirmationCardProps {
  summary: ConfirmationSummary | null;
  disabled: boolean;
  onConfirm: () => void;
  onEdit: (edits: Partial<EnrichedRequirements>) => Promise<void>;
}

export default function WizardConfirmationCard({
  summary,
  disabled,
  onConfirm,
  onEdit,
}: WizardConfirmationCardProps): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [productName, setProductName] = useState(summary?.productName ?? '');
  const [oneLineDescription, setOneLineDescription] = useState(summary?.oneLineDescription ?? '');
  const [businessModel, setBusinessModel] = useState(summary?.businessModel ?? '');
  const [primaryUserType, setPrimaryUserType] = useState(summary?.primaryUserType ?? '');
  const [coreFeaturesJson, setCoreFeaturesJson] = useState(
    summary?.coreFeatures ? JSON.stringify(summary.coreFeatures, null, 2) : '[]',
  );
  const [parseError, setParseError] = useState<string | null>(null);

  // Re-sync local edit state when summary updates after a server-side edit.
  React.useEffect(() => {
    setProductName(summary?.productName ?? '');
    setOneLineDescription(summary?.oneLineDescription ?? '');
    setBusinessModel(summary?.businessModel ?? '');
    setPrimaryUserType(summary?.primaryUserType ?? '');
    setCoreFeaturesJson(
      summary?.coreFeatures ? JSON.stringify(summary.coreFeatures, null, 2) : '[]',
    );
    setParseError(null);
  }, [summary]);

  const handleEditSubmit = () => {
    let parsedFeatures: EnrichedRequirements['coreFeatures'] | undefined;
    try {
      parsedFeatures = JSON.parse(coreFeaturesJson) as EnrichedRequirements['coreFeatures'];
    } catch {
      setParseError('Invalid JSON in core features — fix before saving.');
      return;
    }
    setParseError(null);

    const edits: Partial<EnrichedRequirements> = {};
    if (productName.trim()) edits.productName = productName.trim();
    if (oneLineDescription.trim()) edits.summary = oneLineDescription.trim();
    if (businessModel.trim()) {
      edits.businessModel = businessModel.trim() as EnrichedRequirements['businessModel'];
    }
    if (Array.isArray(parsedFeatures) && parsedFeatures.length > 0) {
      edits.coreFeatures = parsedFeatures;
    }

    setEditing(false);
    onEdit(edits);
  };

  return (
    <div style={cardStyle}>
      <h2 style={titleStyle}>Here's what I understood</h2>

      {!editing ? (
        <>
          {/* Summary display */}
          <div style={summaryGridStyle}>
            {summary?.productName && (
              <SummaryRow label="App name">{summary.productName}</SummaryRow>
            )}
            {summary?.oneLineDescription && (
              <SummaryRow label="What it does">{summary.oneLineDescription}</SummaryRow>
            )}
            {summary?.businessModel && (
              <SummaryRow label="Business model">
                {summary.businessModel.replace(/_/g, ' ')}
              </SummaryRow>
            )}
            {summary?.primaryUserType && (
              <SummaryRow label="Primary user">{summary.primaryUserType}</SummaryRow>
            )}
            {summary?.archetypeHint && (
              <SummaryRow label="Architecture">{summary.archetypeHint}</SummaryRow>
            )}
            {summary?.estimatedBuildTime != null || summary?.estimated_build_time != null ? (
              <SummaryRow label="Estimated build time">
                {Math.round(summary.estimatedBuildTime ?? summary.estimated_build_time ?? 0)} min
              </SummaryRow>
            ) : null}
            {summary?.estimatedCredits != null || summary?.estimated_credits != null ? (
              <SummaryRow label="Estimated cost">
                <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--orange)' }}>
                  ~{Math.round(summary.estimatedCredits ?? summary.estimated_credits ?? 0).toLocaleString()} credits
                </span>
              </SummaryRow>
            ) : null}
            {summary?.coreFeatures && summary.coreFeatures.length > 0 && (
              <SummaryRow label="Core features">
                <ul style={featureListStyle}>
                  {summary.coreFeatures.map((f: { name: string; description: string; priority?: string }) => (
                    <li key={f.name} style={featureItemStyle}>
                      <strong>{f.name}</strong>
                      {f.description ? ` — ${f.description}` : ''}
                      {f.priority && f.priority !== 'must' && (
                        <span style={priorityBadgeStyle}>{f.priority}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </SummaryRow>
            )}
            {summary?.scope_exclusions && summary.scope_exclusions.length > 0 ? (
              <SummaryRow label="Not being built">
                <ul style={featureListStyle}>
                  {summary.scope_exclusions.map((item) => <li key={item} style={featureItemStyle}>{item}</li>)}
                </ul>
              </SummaryRow>
            ) : (
              <SummaryRow label="Not being built">Nothing excluded</SummaryRow>
            )}
          </div>
          <p style={editHintStyle}>Anything I got wrong?</p>
          <div style={ctaRowStyle}>
            <button
              onClick={onConfirm}
              disabled={disabled}
              style={primaryButtonStyle}
              type="button"
            >
              Looks right — build it
            </button>
            <button
              onClick={() => setEditing(true)}
              disabled={disabled}
              style={ghostButtonStyle}
              type="button"
            >
              Edit
            </button>
          </div>
        </>
      ) : (
        /* Inline edit form */
        <div>
          <p style={editHintStyle}>Edit the details below, then save to continue.</p>

          <FormField label="App name">
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              style={inputStyle}
              placeholder="e.g. TaskFlow"
            />
          </FormField>

          <FormField label="One-line description">
            <input
              type="text"
              value={oneLineDescription}
              onChange={(e) => setOneLineDescription(e.target.value)}
              style={inputStyle}
              placeholder="e.g. A tool for gym owners to manage members"
            />
          </FormField>

          <FormField label="Business model">
            <input
              type="text"
              value={businessModel}
              onChange={(e) => setBusinessModel(e.target.value)}
              style={inputStyle}
              placeholder="saas | internal_tool | marketplace | content | unknown"
            />
          </FormField>

          <FormField label="Primary user type">
            <input
              type="text"
              value={primaryUserType}
              onChange={(e) => setPrimaryUserType(e.target.value)}
              style={inputStyle}
              placeholder="e.g. Gym owner"
            />
          </FormField>

          <details style={advancedDetailsStyle}>
            <summary style={advancedSummaryStyle}>Advanced — core features (JSON)</summary>
            <textarea
              value={coreFeaturesJson}
              onChange={(e) => setCoreFeaturesJson(e.target.value)}
              style={jsonTextareaStyle}
              rows={8}
              spellCheck={false}
            />
          </details>

          {parseError && <p style={parseErrorStyle}>{parseError}</p>}

          <div style={ctaRowStyle}>
            <button
              onClick={handleEditSubmit}
              disabled={disabled}
              style={primaryButtonStyle}
              type="button"
            >
              Save changes
            </button>
            <button
              onClick={() => { setEditing(false); setParseError(null); }}
              disabled={disabled}
              style={ghostButtonStyle}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={rowStyle}>
      <div style={rowLabelStyle}>{label}</div>
      <div style={rowValueStyle}>{children}</div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={formFieldStyle}>
      <label style={formLabelStyle}>{label}</label>
      {children}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface, #1a1a2e)',
  border: '1px solid var(--border, #2a2a4a)',
  borderRadius: 12,
  padding: '20px 24px',
  maxWidth: 600,
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text-primary, #e0e0f0)',
};

const summaryGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginBottom: 20,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
};

const rowLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 120,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-secondary, #888)',
  paddingTop: 1,
};

const rowValueStyle: React.CSSProperties = {
  flex: 1,
  fontSize: 14,
  color: 'var(--text-primary, #e0e0f0)',
};

const featureListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const featureItemStyle: React.CSSProperties = {
  fontSize: 13,
};

const priorityBadgeStyle: React.CSSProperties = {
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 4,
  background: 'var(--surface-alt, #2a2a4a)',
  fontSize: 11,
  color: 'var(--text-secondary, #888)',
};

const ctaRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 16,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 600,
  background: 'var(--accent, #4f46e5)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};

const ghostButtonStyle: React.CSSProperties = {
  padding: '9px 18px',
  fontSize: 14,
  fontWeight: 500,
  background: 'transparent',
  color: 'var(--text-secondary, #888)',
  border: '1px solid var(--border, #2a2a4a)',
  borderRadius: 8,
  cursor: 'pointer',
};

const editHintStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: 13,
  color: 'var(--text-secondary, #888)',
};

const formFieldStyle: React.CSSProperties = {
  marginBottom: 12,
};

const formLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-secondary, #888)',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  background: 'var(--input-bg, #0d0d1a)',
  color: 'var(--text-primary, #e0e0f0)',
  border: '1px solid var(--border, #2a2a4a)',
  borderRadius: 6,
  boxSizing: 'border-box',
};

const advancedDetailsStyle: React.CSSProperties = {
  marginBottom: 12,
};

const advancedSummaryStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-secondary, #888)',
  cursor: 'pointer',
  userSelect: 'none',
  marginBottom: 6,
};

const jsonTextareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 12,
  fontFamily: 'monospace',
  background: 'var(--input-bg, #0d0d1a)',
  color: 'var(--text-primary, #e0e0f0)',
  border: '1px solid var(--border, #2a2a4a)',
  borderRadius: 6,
  boxSizing: 'border-box',
  resize: 'vertical',
};

const parseErrorStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 13,
  color: '#ef4444',
};
